/**
 * Workbench org chart: stable ids, parent links, tree build, summary.
 */

import { normalizeRoleToken, displayPerson } from "./parseOrganizationalSetupCsv";

function norm(s) {
  return String(s ?? "")
    .trim()
    .replace(/\s+/g, " ");
}

export function newOrgRowId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `row-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/** Parse "Name (Designation)" or plain name from Reports To cell. */
export function parseReportsToRef(s) {
  const t = norm(s);
  if (!t || t === "-" || t === "-" || t.toLowerCase() === "n/a") return null;
  const m = t.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
  if (m) return { name: norm(m[1]), designation: norm(m[2]) };
  return { name: t, designation: "" };
}

function selfDisplayMatch(raw, selfDisplay) {
  const a = norm(raw).toLowerCase().replace(/\s+/g, " ");
  const b = norm(selfDisplay).toLowerCase().replace(/\s+/g, " ");
  return a === b && a.length > 0;
}

/**
 * Resolve parent row id from reportsToRaw, or null for top level.
 * @param {Array<{id: string, name: string, designation: string, selfDisplay: string, reportsToRaw: string}>} rows
 * @param {number} i row index
 */
function resolveParentId(rows, i) {
  const r = rows[i];
  if (!norm(r.reportsToRaw)) return null;

  const ref = parseReportsToRef(r.reportsToRaw);
  if (!ref) return null;

  const refAsSelfDisplay = displayPerson(ref.name, ref.designation);
  if (
    norm(refAsSelfDisplay).toLowerCase().replace(/\s+/g, " ") ===
    norm(r.selfDisplay).toLowerCase().replace(/\s+/g, " ")
  ) {
    return null;
  }
  if (selfDisplayMatch(r.reportsToRaw, r.selfDisplay)) return null;

  const byName = new Map();
  for (let k = 0; k < rows.length; k++) {
    const key = rows[k].name.toLowerCase();
    if (!byName.has(key)) byName.set(key, []);
    byName.get(key).push(k);
  }

  const wantName = ref.name.toLowerCase();
  const wantDes = ref.designation.toLowerCase();

  let parentIdx = -1;
  for (let j = 0; j < rows.length; j++) {
    if (j === i) continue;
    const o = rows[j];
    if (o.name.toLowerCase() !== wantName) continue;
    if (!wantDes || o.designation.toLowerCase() === wantDes) {
      parentIdx = j;
      break;
    }
  }
  if (parentIdx < 0) {
    const cands = (byName.get(wantName) || []).filter((j) => j !== i);
    if (cands.length === 1) parentIdx = cands[0];
  }
  if (parentIdx < 0 && wantDes) {
    const hits = [];
    for (let j = 0; j < rows.length; j++) {
      if (j === i) continue;
      if (rows[j].designation.toLowerCase() === wantDes) hits.push(j);
    }
    if (hits.length === 1) parentIdx = hits[0];
  }

  if (parentIdx >= 0) return rows[parentIdx].id;
  return null;
}

/** Ensure ids, selfDisplay, roleNormalized, parentId from reportsToRaw. */
export function enrichOrgChartRows(rows) {
  const list = Array.isArray(rows) ? rows.map((r) => ({ ...r })) : [];
  for (let i = 0; i < list.length; i++) {
    if (!list[i].id) list[i].id = newOrgRowId();
    list[i].name = norm(list[i].name);
    list[i].designation = norm(list[i].designation);
    list[i].roleRaw = norm(list[i].roleRaw);
    list[i].department = norm(list[i].department);
    list[i].reportsToRaw = norm(list[i].reportsToRaw);
    list[i].selfDisplay = displayPerson(list[i].name, list[i].designation);
    list[i].roleNormalized = normalizeRoleToken(list[i].roleRaw);
    list[i].index = i;
  }
  for (let i = 0; i < list.length; i++) {
    list[i].parentId = resolveParentId(list, i);
  }
  return list;
}

export function computeOrgChartSummary(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const deptSet = new Set(list.map((r) => r.department).filter(Boolean));
  const withParent = list.filter((r) => r.parentId != null && r.parentId !== "").length;
  return {
    totalPeople: list.length,
    departments: deptSet.size,
    topLevel: list.length - withParent,
    reportingLines: withParent,
  };
}

/** Chart strata: Lead advisor → SPOC/Advisor → Delegate (lower = higher on canvas). */
const ROLE_DISPLAY_TIER = {
  LEAD_ADVISOR: 0,
  COMPANY_SPOC: 1,
  ADVISOR: 1,
  COMPANY_DELEGATE: 2,
  UNKNOWN: 1,
};

export function roleDisplayTier(roleNormalized) {
  return ROLE_DISPLAY_TIER[roleNormalized] ?? 1;
}

function sortNodesByTierThenIndex(a, b) {
  const ta = roleDisplayTier(a.row.roleNormalized);
  const tb = roleDisplayTier(b.row.roleNormalized);
  if (ta !== tb) return ta - tb;
  return (a.row.index ?? 0) - (b.row.index ?? 0);
}

/** @returns {Map<string, { row: object, children: ReturnType<typeof buildTreeNode>[] }>} not used — build forest */
export function buildOrgForest(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const byId = new Map(list.map((r) => [r.id, { row: r, children: [] }]));
  const roots = [];
  for (const r of list) {
    const node = byId.get(r.id);
    const pid = r.parentId;
    if (pid && byId.has(pid)) {
      byId.get(pid).children.push(node);
    } else {
      roots.push(node);
    }
  }
  roots.sort(sortNodesByTierThenIndex);
  const sortRec = (node) => {
    node.children.sort(sortNodesByTierThenIndex);
    node.children.forEach(sortRec);
  };
  roots.forEach(sortRec);
  return roots;
}

/** All descendant ids of `rootId` (not including root). */
export function findDescendantIds(rows, rootId) {
  const byParent = new Map();
  for (const r of rows || []) {
    const p = r.parentId;
    if (!p) continue;
    if (!byParent.has(p)) byParent.set(p, []);
    byParent.get(p).push(r.id);
  }
  const out = new Set();
  const stack = [...(byParent.get(rootId) || [])];
  while (stack.length) {
    const id = stack.pop();
    if (out.has(id)) continue;
    out.add(id);
    const kids = byParent.get(id) || [];
    for (const k of kids) stack.push(k);
  }
  return out;
}

/** True if setting rowId's parent to newParentId creates a cycle. */
export function wouldCreateCycle(rows, rowId, newParentId) {
  if (!newParentId || newParentId === rowId) return true;
  const desc = findDescendantIds(rows, rowId);
  if (desc.has(newParentId)) return true;
  let cur = newParentId;
  const seen = new Set();
  const byId = new Map((rows || []).map((r) => [r.id, r]));
  while (cur) {
    if (cur === rowId) return true;
    if (seen.has(cur)) break;
    seen.add(cur);
    const p = byId.get(cur);
    cur = p?.parentId || null;
  }
  return false;
}

/** Role dropdown values (roleRaw) aligned with normalizeRoleToken. */
export const ORG_FORM_ROLE_OPTIONS = [
  { value: "Lead advisor", token: "LEAD_ADVISOR" },
  { value: "Company SPOC", token: "COMPANY_SPOC" },
  { value: "Company Delegate", token: "COMPANY_DELEGATE" },
  { value: "Advisor", token: "ADVISOR" },
];

export function roleRawFromNormalized(token) {
  const o = ORG_FORM_ROLE_OPTIONS.find((x) => x.token === token);
  return o ? o.value : "Company Delegate";
}
